/**
 * QuickStart information for Weave product.
 */

import {Button} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {ButtonSizes} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {ToggleButtonGroup} from '@wandb/weave/components/ToggleButtonGroup';
import copyToClipboard from 'copy-to-clipboard';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useHistory} from 'react-router-dom';
import {Slide, toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {
  useProjectFromNameQuery,
  useUpsertModelMutation,
  useViewerApiKeyQuery,
} from '../../../generated/graphql';
import * as Track from '../../../services/analytics';
import {QuickStartAnalyticsOrigin} from '../../../services/analytics/quickStartEvents';
import {doNotRetryContext, propagateErrorsContext} from '../../../util/errors';
import {AccessOption} from '../../../util/permissions';
import {weaveColab} from '../../../util/urls';
import {useGetInferenceUsageDetails} from '../../../util/usage/inference';
import {CodeSnippet} from '../../CodeSnippet';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../../Search/SearchNav/types';
import * as S from '../QuickStart.styles';
import {Product} from '../QuickStartProductSelect';

// Define constants
const QUICKSTART_PROJECT_NAME = 'quickstart_playground';

export const Code = styled.code`
  font-family: 'Source Code Pro';
`;
Code.displayName = 'S.Code';

// LLM Provider display names
type LLMProvider = 'W&B Inference' | 'OpenAI' | 'Anthropic';

// Code snippets for different providers
const makeOpenAICodeSnippet = (
  projectName: string,
  entityName: string
) => String.raw`# Ensure your OpenAI client is available with:
# pip install openai

# Ensure that your OpenAI API key is available at:
# os.environ['OPENAI_API_KEY'] = "<your_openai_api_key>"

import os
import weave
from openai import OpenAI

weave.init('${entityName ? `${entityName}/` : ''}${projectName}') # 🐝

@weave.op() # 🐝 Decorator to track requests
def create_completion(message: str) -> str:
    client = OpenAI()
    response = client.chat.completions.create(
        model="gpt-5",
        messages=[
            {"role": "system", "content": "You are a helpful assistant."},
            {"role": "user", "content": message}
        ],
    )
    return response.choices[0].message.content

message = "Tell me a joke."
create_completion(message)`;

const makeAnthropicCodeSnippet = (
  projectName: string,
  entityName: string
) => String.raw`# Ensure your Anthropic client is available with:
# pip install anthropic

# Ensure that your Anthropic API key is available at:
# os.environ['ANTHROPIC_API_KEY'] = "<your_anthropic_api_key>"

import os
import weave
from anthropic import Anthropic

weave.init('${entityName ? `${entityName}/` : ''}${projectName}') # 🐝

@weave.op() # 🐝 Decorator to track requests
def create_completion(message: str) -> str:
    client = Anthropic()
    response = client.messages.create(
        model="claude-opus-4-1-20250805",
        system="You are a helpful assistant.",
        max_tokens=120,
        messages=[
            {"role": "user", "content": message}
        ]
    )
    return response.content[0].text

message = "Tell me a joke."
create_completion(message)`;

const makeWandbInferenceCodeSnippet = (
  projectName: string,
  entityName: string
) => String.raw`# Ensure your OpenAI client is available with:
# pip install openai

# Ensure that your wandb API key is available at:
# os.environ['WANDB_API_KEY'] = "<your_wandb_api_key>"

import os
import weave
from openai import OpenAI

weave.init('${entityName ? `${entityName}/` : ''}${projectName}') # 🐝

@weave.op() # 🐝 Decorator to track requests
def create_completion(message: str) -> str:
  client = OpenAI(
      base_url='https://api.inference.wandb.ai/v1',
      api_key=os.environ['WANDB_API_KEY'], # 🔑 Your wandb API key
      project='${entityName ? `${entityName}/` : ''}${projectName}',
  )
  response = client.chat.completions.create(
      model="OpenPipe/Qwen3-14B-Instruct",
      messages=[
          {"role": "system", "content": "You are a helpful assistant."},
          {"role": "user", "content": message}
      ],
  )
  return response.choices[0].message.content
message = "Tell me a joke."
create_completion(message)
`;

// Evaluation code snippets for different providers
const makeOpenAIEvaluationSnippet = (
  projectName: string,
  entityName: string
) => String.raw`# Ensure your OpenAI client and pandas is installed with:
# pip install openai pandas

# Ensure that your OpenAI API key is available at:
# os.environ['OPENAI_API_KEY'] = "<your_openai_api_key>"

import asyncio
import os
import re
from textwrap import dedent

import openai
import weave


class JsonModel(weave.Model):
    prompt: weave.Prompt = weave.StringPrompt(
        dedent("""
You are an assistant that answers questions about JSON data provided by the user. The JSON data represents structured information of various kinds, and may be deeply nested. In the first user message, you will receive the JSON data under a label called 'context', and a question under a label called 'question'. Your job is to answer the question with as much accuracy and brevity as possible. Give only the answer with no preamble. You must output the answer in XML format, between <answer> and </answer> tags.
""")
    )
    model: str = "gpt-4.1-nano"
    _client: openai.OpenAI

    def __init__(self):
        super().__init__()
        self._client = openai.OpenAI()

    @weave.op
    def predict(self, context: str, question: str) -> str:
        response = self._client.chat.completions.create(
            model=self.model,
            messages=[
                {"role": "system", "content": self.prompt.format()},
                {
                    "role": "user",
                    "content": f"Context: {context}\nQuestion: {question}",
                },
            ],
        )
        assert response.choices[0].message.content is not None
        return response.choices[0].message.content


@weave.op
def correct_answer_format(answer: str, output: str) -> dict[str, bool]:
    parsed_output = re.search(r"<answer>(.*?)</answer>", output, re.DOTALL)
    if parsed_output is None:
        return {"correct_answer": False, "correct_format": False}
    return {"correct_answer": parsed_output.group(1) == answer, "correct_format": True}


if __name__ == "__main__":
    if not os.environ.get('OPENAI_API_KEY'):
        print("OPENAI_API_KEY is not set - make sure to export it in your environment or assign it in this script")
        exit(1)

    weave.init("${entityName ? `${entityName}/` : ''}${projectName}")

    jsonqa = weave.Dataset.from_uri(
        "weave:///wandb/json-qa/object/json-qa:v3"
    ).to_pandas()

    model = JsonModel()

    eval = weave.Evaluation(
        name="json-qa-eval",
        dataset=weave.Dataset.from_pandas(jsonqa),
        scorers=[correct_answer_format],
    )

    asyncio.run(eval.evaluate(model))`;

const makeAnthropicEvaluationSnippet = (
  projectName: string,
  entityName: string
) => String.raw`# Ensure your Anthropic client and pandas is installed with:
# pip install anthropic pandas

# Ensure that your Anthropic API key is available at:
# os.environ['ANTHROPIC_API_KEY'] = "<your_anthropic_api_key>"

import asyncio
import os
import re
from textwrap import dedent

import weave
from anthropic import Anthropic


class JsonModel(weave.Model):
    prompt: weave.Prompt = weave.StringPrompt(
        dedent("""
You are an assistant that answers questions about JSON data provided by the user. The JSON data represents structured information of various kinds, and may be deeply nested. In the first user message, you will receive the JSON data under a label called 'context', and a question under a label called 'question'. Your job is to answer the question with as much accuracy and brevity as possible. Give only the answer with no preamble. You must output the answer in XML format, between <answer> and </answer> tags.
""")
    )
    model: str = "claude-opus-4-1-20250805"
    _client: Anthropic

    def __init__(self):
        super().__init__()
        self._client = Anthropic()

    @weave.op
    def predict(self, context: str, question: str) -> str:
        response = self._client.messages.create(
            model=self.model,
            system=self.prompt.format(),
            max_tokens=120,
            messages=[
                {"role": "user", "content": f"Context: {context}\nQuestion: {question}"}
            ],
        )
        return response.content[0].text


@weave.op
def correct_answer_format(answer: str, output: str) -> dict[str, bool]:
    parsed_output = re.search(r"<answer>(.*?)</answer>", output, re.DOTALL)
    if parsed_output is None:
        return {"correct_answer": False, "correct_format": False}
    return {"correct_answer": parsed_output.group(1) == answer, "correct_format": True}


if __name__ == "__main__":
    weave.init("${entityName ? `${entityName}/` : ''}${projectName}")

    if not os.environ.get('ANTHROPIC_API_KEY'):
        print("ANTHROPIC_API_KEY is not set - make sure to export it in your environment or assign it in this script")
        exit(1)

    jsonqa = weave.Dataset.from_uri(
        "weave:///wandb/json-qa/object/json-qa:v3"
    ).to_pandas()

    model = JsonModel()

    eval = weave.Evaluation(
        name="json-qa-eval",
        dataset=weave.Dataset.from_pandas(jsonqa),
        scorers=[correct_answer_format],
    )

    asyncio.run(eval.evaluate(model))`;

const makeWandbInferenceEvaluationSnippet = (
  projectName: string,
  entityName: string
) => String.raw`# Ensure your OpenAI client and pandas is installed with:
# pip install openai pandas

# Ensure that your wandb API key is available at:
# os.environ['WANDB_API_KEY'] = "<your_wandb_api_key>"

import asyncio
import os
import re
from textwrap import dedent

import weave
from openai import OpenAI


class JsonModel(weave.Model):
    prompt: weave.Prompt = weave.StringPrompt(
        dedent("""
You are an assistant that answers questions about JSON data provided by the user. The JSON data represents structured information of various kinds, and may be deeply nested. In the first user message, you will receive the JSON data under a label called 'context', and a question under a label called 'question'. Your job is to answer the question with as much accuracy and brevity as possible. Give only the answer with no preamble. You must output the answer in XML format, between <answer> and </answer> tags.
""")
    )
    model: str = "OpenPipe/Qwen3-14B-Instruct"
    _client: OpenAI

    def __init__(self):
        super().__init__()
        self._client = OpenAI(
            base_url='https://api.inference.wandb.ai/v1',
            api_key=os.environ['WANDB_API_KEY'],
            project='${entityName ? `${entityName}/` : ''}${projectName}',
        )

    @weave.op
    def predict(self, context: str, question: str) -> str:
        response = self._client.chat.completions.create(
            model=self.model,
            messages=[
                {"role": "system", "content": self.prompt.format()},
                {
                    "role": "user",
                    "content": f"Context: {context}\nQuestion: {question}",
                },
            ],
        )
        return response.choices[0].message.content


@weave.op
def correct_answer_format(answer: str, output: str) -> dict[str, bool]:
    parsed_output = re.search(r"<answer>(.*?)</answer>", output, re.DOTALL)
    if parsed_output is None:
        return {"correct_answer": False, "correct_format": False}
    return {"correct_answer": parsed_output.group(1) == answer, "correct_format": True}


if __name__ == "__main__":
    if not os.environ.get('WANDB_API_KEY'):
        print("WANDB_API_KEY is not set - make sure to export it in your environment or assign it in this script")
        exit(1)

    weave.init("${entityName ? `${entityName}/` : ''}${projectName}")

    jsonqa = weave.Dataset.from_uri(
        "weave:///wandb/json-qa/object/json-qa:v3"
    ).to_pandas()

    model = JsonModel()

    eval = weave.Evaluation(
        name="json-qa-eval",
        dataset=weave.Dataset.from_pandas(jsonqa),
        scorers=[correct_answer_format],
    )

    asyncio.run(eval.evaluate(model))`;

// Provider Note Components
const OpenAIProviderNote = () => (
  <p>
    In this example, we're using a generated OpenAI API key which you can{' '}
    <TargetBlank href="https://platform.openai.com/api-keys">
      find here
    </TargetBlank>
    .
    <br />
    Using another provider? We support all major{' '}
    <TargetBlank href="https://weave-docs.wandb.ai/guides/integrations/">
      clients and frameworks
    </TargetBlank>
    .
  </p>
);

const AnthropicProviderNote = () => (
  <p>
    In this example, we're using a generated Anthropic API key which you can{' '}
    <TargetBlank href="https://console.anthropic.com/settings/keys">
      find here
    </TargetBlank>
    .
    <br />
    Using another provider? We support all major{' '}
    <TargetBlank href="https://weave-docs.wandb.ai/guides/integrations/">
      clients and frameworks
    </TargetBlank>
    .
  </p>
);

const WandbProviderNote = () => (
  <p>
    In this example, we're using W&B Inference.{' '}
    <TargetBlank href="https://docs.wandb.ai/guides/inference/">
      Learn more
    </TargetBlank>{' '}
    about our inference API.
  </p>
);

// Function to get the appropriate provider note component
const getProviderNoteComponent = (provider: LLMProvider) => {
  switch (provider) {
    case 'OpenAI':
      return <OpenAIProviderNote />;
    case 'Anthropic':
      return <AnthropicProviderNote />;
    case 'W&B Inference':
      return <WandbProviderNote />;
    default:
      return <OpenAIProviderNote />;
  }
};

type QuickStartWeaveProps = {
  entityName: string;
  projectName: string;
  analyticsOrigin: QuickStartAnalyticsOrigin;
  maxWidth: number;
  onVariantActivated: (product: Product, variant: string) => void;
};

export const QuickStartWeave = ({
  entityName,
  projectName,
  analyticsOrigin,
  maxWidth,
  onVariantActivated,
}: QuickStartWeaveProps) => {
  // Get inference enablement status
  const {selectedAccount} = useContext(AccountSelectorContext);
  const isPersonalAccount =
    selectedAccount?.accountType === AccountType.Personal;

  // Get inference usage details to check balance and pay-as-you-go status
  const inferenceUsageDetails = useGetInferenceUsageDetails(selectedAccount);
  const {isUsagePaid, limits, usage} = inferenceUsageDetails;

  // Check if user has token balance remaining
  const hasTokenBalance =
    limits.inferenceFreeLimit && usage
      ? limits.inferenceFreeLimit - usage > 0
      : false;

  // User can access W&B Inference if:
  // 1. They have token balance remaining, OR
  // 2. They have pay-as-you-go enabled (isUsagePaid)
  const canAccessInference = hasTokenBalance || isUsagePaid;

  // W&B Inference is enabled if user can access it AND it's not a personal account
  const isInferenceEnabled = canAccessInference && !isPersonalAccount;

  // Set default provider based on inference availability
  const defaultProvider: LLMProvider = isInferenceEnabled
    ? 'W&B Inference'
    : 'OpenAI';
  const [selectedProvider, setSelectedProvider] =
    useState<LLMProvider>(defaultProvider);

  // Options for ToggleButtonGroup - filter out W&B Inference if not enabled
  const providerOptions = [
    ...(isInferenceEnabled ? [{value: 'W&B Inference' as LLMProvider}] : []),
    {value: 'OpenAI' as LLMProvider},
    {value: 'Anthropic' as LLMProvider},
  ];
  const viewerApiKey = useViewerApiKeyQuery();
  const apiKey = viewerApiKey.data?.viewer?.apiKey;
  const hasApiKey = !viewerApiKey.loading && !!apiKey;
  const history = useHistory();

  // Track quickstart viewed on component mount
  useEffect(() => {
    Track.quickStartViewed({
      projectName,
      entityName,
      location: analyticsOrigin,
      quickstartType: 'weave',
      quickstartVariant: 'weave-code-with-colab',
    });
  }, [projectName, entityName, analyticsOrigin]);

  // Playground logic
  const targetProject =
    projectName && projectName !== '' ? projectName : QUICKSTART_PROJECT_NAME;
  const [upsertProjectMutation] = useUpsertModelMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
  });
  const playgroundProjectQuery = useProjectFromNameQuery({
    variables: {
      projectName: targetProject,
      entityName,
    },
  });

  const projectExists = useMemo(() => {
    if (playgroundProjectQuery.loading) {
      return {loading: true, projectExists: undefined};
    } else if (playgroundProjectQuery.data?.project?.id) {
      return {loading: false, projectExists: true};
    } else {
      return {loading: false, projectExists: false};
    }
  }, [playgroundProjectQuery.data, playgroundProjectQuery.loading]);

  const onApiKeyCopyClick = useCallback(() => {
    copyToClipboard(apiKey || '');
    toast('Copied to clipboard', {
      transition: Slide,
    });

    Track.copiedApiKey({
      projectName,
      entityName,
      location: analyticsOrigin,
      frameworkName: 'weave',
    });
  }, [analyticsOrigin, apiKey, entityName, projectName]);

  const projectNameStr = projectName === '' ? 'intro-example' : projectName;

  // Get code snippet based on selected provider
  const getCodeSnippet = () => {
    switch (selectedProvider) {
      case 'OpenAI':
        return makeOpenAICodeSnippet(projectNameStr, entityName);
      case 'Anthropic':
        return makeAnthropicCodeSnippet(projectNameStr, entityName);
      case 'W&B Inference':
        return makeWandbInferenceCodeSnippet(projectNameStr, entityName);
      default:
        return makeOpenAICodeSnippet(projectNameStr, entityName);
    }
  };

  const codeSnippet = getCodeSnippet();
  const getEvaluationSnippet = () => {
    switch (selectedProvider) {
      case 'OpenAI':
        return makeOpenAIEvaluationSnippet(projectNameStr, entityName);
      case 'Anthropic':
        return makeAnthropicEvaluationSnippet(projectNameStr, entityName);
      case 'W&B Inference':
        return makeWandbInferenceEvaluationSnippet(projectNameStr, entityName);
      default:
        return makeOpenAIEvaluationSnippet(projectNameStr, entityName);
    }
  };
  const evaluationSnippet = getEvaluationSnippet();
  const colabLink = weaveColab();

  return (
    <>
      <S.QuickStartHeader>
        <S.QuickStartHeaderText>
          {' '}
          Quickstart: Creating your first trace in W&B Weave
        </S.QuickStartHeaderText>
        <p>Weave is a toolkit for developing AI-powered applications.</p>
        <div>
          You can use Weave to:
          <ul>
            <li>Log and debug language model inputs, outputs, and traces</li>
            <li>
              Build rigorous, apples-to-apples evaluations for language model
              use cases
            </li>
            <li>
              Organize all the data generated across the LLM workflow, from
              experimentation to evaluation to production
            </li>
          </ul>
          Start by logging a trace—either from the playground or right from your
          code. Visit{' '}
          <TargetBlank href="https://wandb.me/weave">
            our documentation
          </TargetBlank>{' '}
          to learn more.
        </div>
      </S.QuickStartHeader>

      <div>
        <S.QuickStartOrderedList>
          <S.QuickStartListItem>Set up the Weave library</S.QuickStartListItem>
          <S.QuickStartStepContent>
            <p>
              Install the CLI and Python library for interacting with Weave and
              Wandb.
            </p>
            <CodeSnippet
              codeSnippet="pip install wandb weave"
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave',
              }}
            />
            <p>Next, login to wandb and paste your API key when prompted.</p>
            <CodeSnippet
              codeSnippet="wandb login"
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave',
              }}
            />
            {hasApiKey && (
              <S.UserApiKeyRow analyticsOrigin={analyticsOrigin}>
                <S.UserApiKeyLabel>
                  <S.QuickStartIcon>
                    <Icon name="key-admin" />
                  </S.QuickStartIcon>
                  <p className="mr-auto">
                    <b>Your API key</b> for logging in to the wandb library.
                  </p>
                </S.UserApiKeyLabel>
                <S.UserApiKeyBox onClick={onApiKeyCopyClick}>
                  <p>{apiKey}</p>
                  <S.QuickStartIcon>
                    <Icon name="copy" />
                  </S.QuickStartIcon>
                </S.UserApiKeyBox>
              </S.UserApiKeyRow>
            )}
            <p>
              You can also set your API key with the following environment
              variable.
            </p>
            <CodeSnippet
              codeSnippet={`import os\nos.environ['WANDB_API_KEY'] = '${
                hasApiKey ? apiKey : 'your_api_key'
              }'`}
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave',
              }}
            />
          </S.QuickStartStepContent>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
            }}>
            <S.QuickStartListItem
              style={{marginBottom: '0', marginRight: '8px'}}>
              Log a trace with code or
            </S.QuickStartListItem>
            <TargetBlank href={colabLink}>
              <S.ColabButton
                variant="contained"
                color="default"
                size="small"
                onClick={() => {
                  Track.clickedOpenInColab({
                    projectName,
                    entityName,
                    location: analyticsOrigin,
                    frameworkName: 'Weave - None Specified',
                    colabLink,
                  });
                }}
                startIcon={
                  <S.QuickStartIcon>
                    <Icon name="logo-colab" />
                  </S.QuickStartIcon>
                }>
                Open in Colab
              </S.ColabButton>
            </TargetBlank>
          </div>
          <S.QuickStartStepContent>
            <p>
              Start tracking inputs and outputs of functions by decorating them
              with <Code>weave.op()</Code>.<br />
              Run this sample code to see the new trace.
            </p>

            <div style={{marginBottom: '8px'}}>
              <ToggleButtonGroup
                options={providerOptions}
                value={selectedProvider}
                size={ButtonSizes.Small}
                onValueChange={(provider: string) => {
                  setSelectedProvider(provider as LLMProvider);
                }}
              />
            </div>
            {getProviderNoteComponent(selectedProvider)}
            <CodeSnippet
              showExpandCollapse={false}
              codeSnippet={codeSnippet}
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave',
              }}
            />
          </S.QuickStartStepContent>
          <S.QuickStartListItem>
            Running your first evaluation
          </S.QuickStartListItem>
          <S.QuickStartStepContent>
            <p>
              Evaluate a simple JSON QA task with Weave evaluators. Pick your
              provider below and run the snippet to log results.
            </p>
            <div style={{marginBottom: '8px'}}>
              <ToggleButtonGroup
                options={providerOptions}
                value={selectedProvider}
                size={ButtonSizes.Small}
                onValueChange={(provider: string) => {
                  setSelectedProvider(provider as LLMProvider);
                }}
              />
            </div>
            {getProviderNoteComponent(selectedProvider)}
            <CodeSnippet
              showExpandCollapse={true}
              codeSnippet={evaluationSnippet}
              analyticsEvent={Track.copiedCodeBlock}
              analyticsDefaultProperties={{
                projectName,
                entityName,
                location: analyticsOrigin,
                frameworkName: 'weave-evaluation',
              }}
            />
          </S.QuickStartStepContent>
          <S.QuickStartListItem>
            Get started with Playground
          </S.QuickStartListItem>
          <S.QuickStartStepContent>
            <p>
              You can interactively develop, review, and test their prompts
              using our LLM playground which supports all major model providers.
            </p>
            <Tailwind className="h-screen">
              <div className="flex w-[800px] content-start justify-between">
                <div className="my-6 flex-initial">
                  <Button
                    isDisabled={projectExists.loading}
                    size="md"
                    endIcon="chevron-next"
                    onPress={async () => {
                      try {
                        Track.clickedWeavePlayground({
                          projectName: targetProject,
                          entityName,
                          location: analyticsOrigin,
                        });
                        if (!projectExists.projectExists) {
                          await upsertProjectMutation({
                            variables: {
                              description: QUICKSTART_PROJECT_NAME,
                              access: AccessOption.Private,
                              name: targetProject,
                              entityName,
                            },
                          });
                        }
                        history.push(
                          `/${entityName}/${targetProject}/weave/playground`
                        );
                      } catch (e) {
                        toast.error(
                          'Failed to create project: Please contact team admin to create a project.'
                        );
                        console.error(e);
                      }
                    }}>
                    Try playground
                  </Button>
                </div>
              </div>
            </Tailwind>
          </S.QuickStartStepContent>
        </S.QuickStartOrderedList>
      </div>
    </>
  );
};
